import { useCallback } from 'react';
import { useIntl } from 'dibs-react-intl';
import { constants } from 'ethers';
import { useFragment, graphql } from 'react-relay';
import { type useEtherConversion_etherRates$key } from './__generated__/useEtherConversion_etherRates.graphql.ts';

export type GetConvertedEther = (eth: string, forcedCurrency?: string) => string;
export type GetConvertedEtherUnformatted = (eth: string, forcedCurrency?: string) => string;
export type GetAmountStrings = (eth: string) => {
    ether: string;
    etherNoSymbol: string;
    converted: string;
    etherWithConversion: string;
};

/**
 *
 * @param ether ether amount with ether symbol
 * @returns ether amount with no ether symbol
 */
function stripEthSymbol(ether: string): string {
    return ether.replace(constants.EtherSymbol, '').trim();
}

/**
 *
 * @param ether ether amount with ether symbol and commas
 * @returns ether amount with no ether symbol and no commas
 */
function stripEthSymbolAndCommas(ether: string): string {
    return stripEthSymbol(ether).replace(/,/g, '').trim();
}

export function useEtherConversion({
    currency,
    etherRates: etherRatesRef,
}: {
    currency: string;
    etherRates: useEtherConversion_etherRates$key | null | undefined;
}): {
    getConvertedEther: GetConvertedEther;
    getConvertedEtherUnformatted: GetConvertedEtherUnformatted;
    getAmountStrings: GetAmountStrings;
} {
    const intl = useIntl();
    const etherRates = useFragment(
        graphql`
            fragment useEtherConversion_etherRates on EtherRates @relay(plural: true) {
                currency
                rate
            }
        `,
        etherRatesRef
    );
    const rateObj = etherRates?.find(rate => rate.currency === currency);

    const formatPriceString = useCallback(
        (amount: number, currencyString: string, decimals?: number): string => {
            return intl.formatNumber(amount, {
                style: 'currency',
                currency: currencyString,
                maximumFractionDigits: decimals,
                minimumFractionDigits: decimals,
            });
        },
        [intl]
    );

    const getConvertedEther: GetConvertedEther = useCallback(
        (ether, forcedCurrency) => {
            let conversionRate = rateObj?.rate || 1;
            if (forcedCurrency) {
                const forcedRateObj = etherRates?.find(rate => rate.currency === forcedCurrency);
                conversionRate = forcedRateObj?.rate || 1;
            }
            const convertedAmount = parseFloat(stripEthSymbolAndCommas(ether)) * conversionRate;
            return formatPriceString(convertedAmount, forcedCurrency || currency);
        },
        [currency, etherRates, formatPriceString, rateObj]
    );

    const getConvertedEtherUnformatted: GetConvertedEtherUnformatted = useCallback(
        (ether, forcedCurrency) => {
            const convertedEther = getConvertedEther(ether, forcedCurrency);
            return convertedEther.replace(/[^\d.-]/g, ''); // remove currency sign, keep decimals
        },
        [getConvertedEther]
    );

    const getAmountStrings: GetAmountStrings = useCallback(
        ether => {
            const parsedEther = parseFloat(stripEthSymbolAndCommas(ether)) || 0;

            const convertedAmount = parsedEther * (rateObj?.rate || 1);
            const converted = formatPriceString(convertedAmount, currency);
            const formattedEther = formatPriceString(parsedEther, 'ETH', 4).replace(
                'ETH',
                constants.EtherSymbol
            );
            const etherWithConversion = `${formattedEther} (${converted})`;
            return {
                ether: formattedEther,
                etherNoSymbol: stripEthSymbol(formattedEther),
                converted,
                etherWithConversion,
            };
        },
        [currency, formatPriceString, rateObj]
    );

    return { getConvertedEther, getConvertedEtherUnformatted, getAmountStrings };
}
